import * as loadjs from 'loadjs'
import { getQueryParam } from '../helpers/getQueryParam'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import Logger from '../helpers/Logger'
import type WebModel from '../models/WebModel'

const GROW_FAVES_URL = 'https://faves.grow.me'
const GROW_TIMEOUT = 4000 //ms

export default class GrowFaves {
  static hasLoggedInUser = false
  static growIsLoading: boolean

  /**
   * This Promise will resolve once GrowFaves has loaded.
   * Or it will reject if it times out, or other errors occur.
   **/
  static growHasLoaded: Promise<void>

  constructor(model: WebModel) {
    let favesUrl = `${GROW_FAVES_URL}/main.js`

    const growVersion = getQueryParam('growFavesVersion')
    const growFavesSiteId = getQueryParam('growFavesSiteId') as string

    if (growVersion) {
      favesUrl = `${GROW_FAVES_URL}/main.${growVersion}.js`
    }

    /** Load Grow Faves if query parameter exists
     * or if enabled in model
     */
    if (growFavesSiteId || (model.growFavesEnabled && model.growSiteID)) {
      GrowFaves.growIsLoading = true

      this.setUpGrowQueue()

      GrowFaves.growHasLoaded = new Promise((resolve, reject) => {
        loadjs(favesUrl, LoadJsBundle.growFaves, {
          before: function (path, scriptEl: HTMLScriptElement) {
            // Set attributes on the growFave script tag.
            scriptEl.setAttribute(
              'data-grow-faves-site-id',
              growFavesSiteId ? growFavesSiteId : model.growSiteID
            )
          },
          success() {
            window.growMe(() => {
              window.growMe.on('loaded', resolve)
              window.growMe.on(
                'authStatusChanged',
                (status: { isLoggedIn: boolean }) => {
                  GrowFaves.hasLoggedInUser = status.isLoggedIn
                }
              )
              window.growMe.on(
                'tradeDeskAdvertisingTokenChanged',
                ({ advertisingToken }) =>
                  (window.$mediavine.uid2 = advertisingToken)
              )
            })
          },
          error(error) {
            reject(error)
          }
        })
        // If we've waited 4 seconds, stop waiting.
        setTimeout(() => {
          Logger.debug('Grow.me load timed out.')
          reject(`Grow Timeout (${GROW_TIMEOUT} ms) Exceeded.`)
        }, GROW_TIMEOUT)
      })
    } else {
      GrowFaves.growIsLoading = false
      GrowFaves.growHasLoaded = Promise.reject('Grow is not supposed to load.')
      Logger.debug(
        'ERROR: both ?growFavesAccountId and ?growFavesSiteId must be specified to load GrowFaves'
      )
    }
  }

  private setUpGrowQueue() {
    if (!window.growMe) {
      window.growMe = (cb) => {
        window.growMe._.push(cb)
      }
      window.growMe._ = []
    }
  }

  static willGrowLoad(): boolean {
    const model = window.$mediavine.web.model
    const growFavesSiteId = getQueryParam('growFavesSiteId') as string
    return !!growFavesSiteId || !!(model.growFavesEnabled && model.growSiteID)
  }
}
